import {useState, useRef, useCallback, useEffect} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {artistRegEx as appleMusicArtistRegex} from '../../../../helpers/appleMusic'

import TextField from '@mui/material/TextField'
import CloseIcon from '@mui/icons-material/Close'

import {SiApplemusic} from 'react-icons/si'



const styles = {
  linksContainer: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 20px 0;
  `,
  linkItem: css`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 5px;
    p {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `,
  button: css`
    font-size: 0.8rem;
    color: rgb(62, 166, 255);
    cursor: pointer;
    font-weight: 500;
  `,
  nameContainer: css`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.93rem;
    font-weight: 500;
  `,
  appleMusicIcon: css`
    width: 24px;
    height: 24px;
    color: #fc3c44;
  `,
  closeIcon: css`
    cursor: pointer;
    margin-left: 10px;
    color: grey;
  `,
}


// helper to pull the numeric ID out of the Apple Music URL:
function parseAppleMusicArtistId(urlString) {
  try {
    const parts = new URL(urlString).pathname.split('/').filter(Boolean)
    const last = parts[parts.length - 1]
    return /^\d+$/.test(last) ? last : null
  } catch {
    return null
  }
}

// call iTunes lookup API
async function fetchArtistNameFromUrl(url) {
  const artistId = parseAppleMusicArtistId(url)
  if (!artistId) return ''
  const res = await fetch(`https://itunes.apple.com/lookup?id=${artistId}`)
  const json = await res.json()
  return json.resultCount > 0 ? json.results[0].artistName : ''
}


const AppleMusicLinksField = ({
  field: { name, value = [] },
  form:  { errors, touched, isSubmitting, setFieldValue, setFieldError, setFieldTouched },
  disabled,
  helperText,
  ...props
}) => {
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef(null)

  const [artistNames, setArtistNames] = useState({})

  const isError = touched[name] && Boolean(errors[name])
  const errorText = isError ? errors[name] : helperText

  const isValidURL = useCallback(url => appleMusicArtistRegex.test(url), [])


  // whenever `value` changes, kick off lookups for any new URLs
  useEffect(() => {
    value.forEach(url => {
      if (!(url in artistNames)) {
        // prime the cache as “loading”
        setArtistNames(prev => ({ ...prev, [url]: null }))
        fetchArtistNameFromUrl(url)
          .then(name =>
            setArtistNames(prev => ({ ...prev, [url]: name || 'Unknown Artist' }))
          )
      }
    })
  }, [value, artistNames])


  const clearInput = () => {
    setInputValue('')
    if (inputRef.current) inputRef.current.value = ''
  }


  const addLink = useCallback(() => {
    const url = inputValue.trim()
    if (!url) return

    // Prevent duplicates
    if (value.includes(url)) {
      clearInput()
      return
    }

    // Validate
    if (!isValidURL(url)) {
      setFieldError(name, 'Invalid URL')
      return
    }

    const updated = [...value, url]
    setFieldValue(name, updated)
    clearInput()
    if (errors[name]) setFieldError(name, undefined)
  }, [inputValue, value, name, setFieldValue, setFieldError, errors, isValidURL])


  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addLink()
    }
  }


  const handleChange = e => {
    const val = e.target.value
    setInputValue(val)

    if (isError && isValidURL(val)) {
      setFieldError(name, undefined)
    }
  }


  const handleFocus = () => setFieldTouched(name, true)


  const removeLink = useCallback(
    (link) => setFieldValue(name, value.filter(l => l !== link)),
    [name, value, setFieldValue]
  )



  return (
    <div>
      <TextField
        {...props}
        name={name}
        inputRef={inputRef}
        value={inputValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        error={isError}
        helperText={errorText}
        disabled={disabled || isSubmitting}
        InputProps={{
          endAdornment: inputValue && (
            <div css={styles.button} onClick={addLink}>
              ENTER
            </div>
          )
        }}
      />

      <div css={styles.linksContainer}>
        {value.map(link => {
          const artistName = artistNames[link]
          return (
            <div key={link} css={styles.linkItem}>
              <div css={styles.nameContainer}>
                <SiApplemusic css={styles.appleMusicIcon} />
                <p>
                  {artistName == null ? 'Loading…' : artistName}
                </p>
              </div>
              <CloseIcon 
                css={styles.closeIcon} 
                onClick={() => removeLink(link)} 
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default AppleMusicLinksField



